import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ClipboardList,
  MessageSquare,
  FileText,
  Landmark,
  User,
  X,
} from 'lucide-react';
import { EMPLOYMENT_STATUS_LABELS, formatLongDate, formatShortDate } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn, money } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Modal,
  ModalHeader,
  PageHeader,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { GuarantorCalls } from '@/components/GuarantorCalls';
import { APPLICATION_STATUS, KYC_STATUS, MEMBER_STATUS } from '@/lib/status';

type Decision = 'APPROVE' | 'REJECT' | 'REQUEST_INFORMATION';

interface ApplicationDetail {
  id: string;
  applicationNumber: string;
  status: string;
  requestedAmount: string;
  approvedAmount: string | null;
  installmentCount: number;
  approvedInstallmentCount: number | null;
  purpose: string;
  expectedDisbursementDate: string | null;
  firstDueDate: string | null;
  schedule: Array<{ installmentNumber: number; dueDate: string; amount: string }>;
  installmentAmount: string;
  affordability: {
    employmentStatus: string | null;
    employerName: string | null;
    monthlyIncomeBand: string | null;
    repaymentSource: string | null;
    hasOtherLoans: boolean;
    otherObligations: string | null;
  };
  product: {
    id: string;
    name: string;
    repaymentFrequency: string;
    requiredGuarantors: number;
    maximumAmount: string;
    maximumInstallments: number;
  };
  member: {
    id: string;
    membershipNumber: string;
    name: string;
    email: string;
    phone: string;
    memberSince: string;
    status: string;
    kycStatus: string;
    kycApprovedAt: string | null;
    borrowingSuspendedUntil: string | null;
    activeLoans: number;
    outstanding: string;
    primaryBankAccount: {
      bankName: string;
      accountNumber: string;
      accountName: string;
      verifiedAt: string | null;
    } | null;
  };
  guarantorsRequired: number;
  guarantorsConfirmed: number;
  eligibilitySnapshot: {
    eligible?: boolean;
    reasons?: string[];
    warnings?: string[];
  } | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionReason: string | null;
  internalNotes: string | null;
  loan: { id: string; loanNumber: string; status: string; outstanding: string } | null;
  history: Array<{
    previousStatus: string | null;
    newStatus: string;
    reason: string | null;
    actor: string | null;
    at: string;
  }>;
}

/**
 * One application, in full.
 *
 * A reviewer approving from a table row is approving a number. This page is
 * the evidence: what the borrower asked for, how they say they will repay it,
 * what they already owe, who is standing for them and whether those people have
 * actually been telephoned.
 */
export default function AdminApplicationDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<Decision | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['application', id],
    queryFn: () => api.get<ApplicationDetail>(`/loan-applications/${id}`),
    enabled: Boolean(id),
  });

  const beginReview = useMutation({
    mutationFn: () => api.post(`/loan-applications/${id}/begin-review`, {}),
    onSuccess: () => {
      toast.success('Application taken into review.');
      void queryClient.invalidateQueries({ queryKey: ['application', id] });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not start the review.'),
  });

  if (isLoading) return <Skeleton className="h-96" />;
  if (!data) {
    return (
      <EmptyState
        icon={<FileText size={20} />}
        title="Application not found"
        action={<Button onClick={() => navigate('/admin/applications')}>Back</Button>}
      />
    );
  }

  const guarantorsMet = data.guarantorsConfirmed >= data.guarantorsRequired;
  const canDecide = data.status === 'UNDER_REVIEW';
  const canBeginReview = data.status === 'SUBMITTED';

  return (
    <div>
      <Link
        to="/admin/applications"
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-secondary transition-colors hover:text-foreground"
      >
        <ArrowLeft size={15} /> All applications
      </Link>

      <PageHeader
        eyebrow="Loan application"
        title={data.applicationNumber}
        description={`${data.product.name} · ${data.installmentCount} repayments${
          data.submittedAt ? ` · submitted ${formatLongDate(new Date(data.submittedAt))}` : ''
        }`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge map={APPLICATION_STATUS} status={data.status} />
            {canBeginReview && (
              <Button loading={beginReview.isPending} onClick={() => beginReview.mutate()}>
                <ClipboardList size={15} /> Begin review
              </Button>
            )}
            {canDecide && (
              <>
                <Button variant="ghost" onClick={() => setDecision('REQUEST_INFORMATION')}>
                  <MessageSquare size={15} /> Request more
                </Button>
                <Button variant="outline" onClick={() => setDecision('REJECT')}>
                  <X size={15} /> Decline
                </Button>
                <Button disabled={!guarantorsMet} onClick={() => setDecision('APPROVE')}>
                  <Check size={15} /> Approve
                </Button>
              </>
            )}
          </div>
        }
      />

      {canDecide && !guarantorsMet && (
        <Alert tone="warning" title="Guarantors not yet confirmed" className="mb-6">
          {data.guarantorsConfirmed} of {data.guarantorsRequired} guarantors have been reached and
          confirmed by telephone. Approval stays locked until they are — the calls are the entire
          evidentiary basis of the guarantee.
        </Alert>
      )}

      {data.loan && (
        <Alert tone="success" title="Approved and disbursed as a loan" className="mb-6">
          <Link
            to={`/admin/loans?q=${data.loan.loanNumber}`}
            className="inline-flex items-center gap-1.5 font-bold underline"
          >
            {data.loan.loanNumber} <ArrowRight size={13} />
          </Link>{' '}
          — {money(data.loan.outstanding)} outstanding.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* The request */}
          <Card>
            <CardHeader>
              <CardTitle>The request</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid gap-4 sm:grid-cols-3">
                <Figure label="Requested" value={money(data.requestedAmount)} />
                <Figure
                  label="Repayments"
                  value={`${data.installmentCount} × ${money(data.installmentAmount)}`}
                />
                <Figure
                  label="Total repayable"
                  value={money(data.approvedAmount ?? data.requestedAmount)}
                  hint="Qard Hasan — equal to the principal"
                  tone="success"
                />
              </div>

              <div className="mt-5 grid gap-x-8 gap-y-3 border-t border-border pt-5 sm:grid-cols-2">
                <DetailRow label="Purpose" value={data.purpose} className="sm:col-span-2" />
                <DetailRow
                  label="Expected disbursement"
                  value={
                    data.expectedDisbursementDate
                      ? formatLongDate(new Date(data.expectedDisbursementDate))
                      : null
                  }
                />
                <DetailRow
                  label="First repayment due"
                  value={data.firstDueDate ? formatLongDate(new Date(data.firstDueDate)) : null}
                />
              </div>
            </CardContent>
          </Card>

          {/* Affordability */}
          <Card>
            <CardHeader>
              <CardTitle>How they intend to repay</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                <DetailRow
                  label="Employment"
                  value={
                    data.affordability.employmentStatus
                      ? (EMPLOYMENT_STATUS_LABELS[
                          data.affordability
                            .employmentStatus as keyof typeof EMPLOYMENT_STATUS_LABELS
                        ] ?? data.affordability.employmentStatus)
                      : null
                  }
                />
                <DetailRow label="Employer" value={data.affordability.employerName} />
                <DetailRow label="Monthly income" value={data.affordability.monthlyIncomeBand} />
                <DetailRow
                  label="Other loans elsewhere"
                  value={data.affordability.hasOtherLoans ? 'Yes' : 'No'}
                />
                <DetailRow
                  label="Source of repayment"
                  value={data.affordability.repaymentSource}
                  className="sm:col-span-2"
                />
                {data.affordability.otherObligations && (
                  <DetailRow
                    label="Other obligations"
                    value={data.affordability.otherObligations}
                    className="sm:col-span-2"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Guarantor calls — the same component the queue uses. */}
          <GuarantorCalls
            applicationId={data.id}
            requiredCount={data.guarantorsRequired}
            onChange={() => {
              void queryClient.invalidateQueries({ queryKey: ['application', id] });
            }}
          />

          {/* Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>Proposed schedule</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <Table>
                <thead>
                  <tr>
                    <Th numeric>#</Th>
                    <Th>Due</Th>
                    <Th numeric>Amount</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.schedule.map((installment) => (
                    <tr key={installment.installmentNumber}>
                      <Td numeric className="text-foreground-secondary">
                        {installment.installmentNumber}
                      </Td>
                      <Td className="numeric">
                        {formatShortDate(new Date(installment.dueDate))}
                      </Td>
                      <Td numeric className="font-semibold">
                        {money(installment.amount)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardContent>
          </Card>

          {data.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>History</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ol className="space-y-3">
                  {data.history.map((entry, index) => (
                    <li key={index} className="flex gap-3">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                          {APPLICATION_STATUS[entry.newStatus as keyof typeof APPLICATION_STATUS]
                            ?.label ?? entry.newStatus}
                        </p>
                        <p className="text-xs text-foreground-secondary">
                          {entry.actor ? `${entry.actor} · ` : ''}
                          {formatLongDate(new Date(entry.at))}
                        </p>
                        {entry.reason && (
                          <p className="mt-0.5 text-sm text-foreground-secondary">
                            &ldquo;{entry.reason}&rdquo;
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}
        </div>

        {/* The applicant */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Applicant</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Link
                to={`/admin/members/${data.member.id}`}
                className="mb-4 flex items-center gap-3 rounded-xl border border-border p-3.5 transition-colors hover:bg-accent"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  <User size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-foreground">{data.member.name}</p>
                  <p className="numeric text-xs text-foreground-secondary">
                    {data.member.membershipNumber}
                  </p>
                </div>
                <ArrowRight size={15} className="shrink-0 text-foreground-secondary" />
              </Link>

              <div className="mb-4 flex flex-wrap gap-2">
                <StatusBadge map={MEMBER_STATUS} status={data.member.status} />
                <StatusBadge map={KYC_STATUS} status={data.member.kycStatus} />
              </div>

              <div className="space-y-3">
                <DetailRow label="Telephone" value={data.member.phone} numeric />
                <DetailRow label="Email" value={data.member.email} />
                <DetailRow
                  label="Member since"
                  value={formatLongDate(new Date(data.member.memberSince))}
                />
                <DetailRow
                  label="Already owes"
                  value={`${money(data.member.outstanding)} across ${data.member.activeLoans} active loans`}
                />
              </div>

              {data.member.kycStatus !== 'APPROVED' && (
                <Alert tone="warning" className="mt-4">
                  KYC is not approved. A loan should not be disbursed to an unverified member.
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Disbursement account</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {data.member.primaryBankAccount ? (
                <div className="rounded-xl border border-border p-3.5">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Landmark size={14} className="text-foreground-secondary" />
                    {data.member.primaryBankAccount.bankName}
                  </p>
                  <p className="numeric mt-1 text-sm text-foreground">
                    {data.member.primaryBankAccount.accountNumber}
                  </p>
                  <p className="text-xs text-foreground-secondary">
                    {data.member.primaryBankAccount.accountName}
                  </p>
                </div>
              ) : (
                <Alert tone="warning">
                  No bank account on file. This loan cannot be disbursed until the member adds one.
                </Alert>
              )}
            </CardContent>
          </Card>

          {data.eligibilitySnapshot && (
            <Card>
              <CardHeader>
                <CardTitle>Eligibility at submission</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="mb-3 text-xs leading-relaxed text-foreground-secondary">
                  Recorded when the application was submitted, so a later change to product rules
                  never rewrites why something was approved.
                </p>
                <div
                  className={cn(
                    'rounded-xl px-3.5 py-2.5 text-sm font-semibold',
                    data.eligibilitySnapshot.eligible
                      ? 'bg-success-soft text-success-soft-foreground'
                      : 'bg-danger-soft text-danger-soft-foreground',
                  )}
                >
                  {data.eligibilitySnapshot.eligible ? 'Eligible' : 'Not eligible'}
                </div>
                {(data.eligibilitySnapshot.reasons ?? []).map((reason) => (
                  <p key={reason} className="mt-2 flex gap-1.5 text-sm text-danger">
                    <X size={14} className="mt-0.5 shrink-0" /> {reason}
                  </p>
                ))}
                {(data.eligibilitySnapshot.warnings ?? []).map((warning) => (
                  <p key={warning} className="mt-2 flex gap-1.5 text-sm text-warning">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {warning}
                  </p>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Product</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <DetailRow label="Name" value={data.product.name} />
              <DetailRow
                label="Repayment frequency"
                value={data.product.repaymentFrequency.toLowerCase()}
              />
              <DetailRow label="Maximum" value={money(data.product.maximumAmount)} />
              <DetailRow
                label="Guarantors required"
                value={String(data.product.requiredGuarantors)}
              />
            </CardContent>
          </Card>

          {data.decidedAt && (
            <Card>
              <CardHeader>
                <CardTitle>Decision</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <DetailRow label="Decided by" value={data.decidedBy} />
                <DetailRow label="On" value={formatLongDate(new Date(data.decidedAt))} />
                {data.approvedAmount && (
                  <DetailRow label="Approved amount" value={money(data.approvedAmount)} />
                )}
                {data.decisionReason && (
                  <DetailRow label="Reason" value={data.decisionReason} />
                )}
                {data.internalNotes && (
                  <DetailRow label="Internal notes" value={data.internalNotes} />
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {decision && (
        <DecisionModal
          application={data}
          decision={decision}
          onClose={() => setDecision(null)}
          onDone={() => {
            setDecision(null);
            void queryClient.invalidateQueries({ queryKey: ['application', id] });
            void queryClient.invalidateQueries({ queryKey: ['applications'] });
          }}
        />
      )}
    </div>
  );
}

function DecisionModal({
  application,
  decision,
  onClose,
  onDone,
}: {
  application: ApplicationDetail;
  decision: Decision;
  onClose: () => void;
  onDone: () => void;
}) {
  const approving = decision === 'APPROVE';
  // The API takes naira here, not kobo - positiveNairaAmountSchema.
  const [approvedAmount, setApprovedAmount] = useState(
    (Number(application.requestedAmount) / 100).toFixed(2),
  );
  const [installmentCount, setInstallmentCount] = useState(application.installmentCount);
  const [reason, setReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const TITLES: Record<Decision, string> = {
    APPROVE: 'Approve application',
    REJECT: 'Decline application',
    REQUEST_INFORMATION: 'Request more information',
  };

  const decide = useMutation({
    mutationFn: () =>
      api.post('/loan-applications/decide', {
        applicationId: application.id,
        decision,
        ...(approving ? { approvedAmount, approvedInstallmentCount: installmentCount } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(internalNotes.trim() ? { internalNotes: internalNotes.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success(
        approving
          ? 'Approved. The loan and its schedule have been created.'
          : decision === 'REJECT'
            ? 'Application declined.'
            : 'More information requested from the member.',
      );
      onDone();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not record that decision.'),
  });

  return (
    <Modal open onClose={onClose}>
      <ModalHeader
        title={TITLES[decision]}
        description={`${application.applicationNumber} - ${application.member.name}`}
        onClose={onClose}
      />

      <div className="space-y-4 p-6">
        {approving && (
          <>
            <Field
              label="Amount to approve"
              htmlFor="amount"
              hint="In naira. May be below what was requested."
            >
              <Input
                id="amount"
                className="numeric"
                value={approvedAmount}
                onChange={(e) => setApprovedAmount(e.target.value)}
              />
            </Field>
            <Field label="Number of repayments" htmlFor="installments">
              <Input
                id="installments"
                type="number"
                className="numeric"
                value={installmentCount}
                onChange={(e) => setInstallmentCount(Number(e.target.value))}
              />
            </Field>
            <Alert tone="info">
              The member repays exactly this amount. No interest, fee or charge is added at any
              point in the life of the loan.
            </Alert>
          </>
        )}

        <Field
          label={approving ? 'Note to the member (optional)' : 'Reason'}
          htmlFor="reason"
          hint="The member sees this."
        >
          <Textarea id="reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>

        <Field label="Internal notes (optional)" htmlFor="notes" hint="Never shown to the member.">
          <Textarea
            id="notes"
            rows={2}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
          />
        </Field>
      </div>

      <div className="flex justify-end gap-2 border-t border-border p-4">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={approving ? 'primary' : decision === 'REJECT' ? 'danger' : 'secondary'}
          loading={decide.isPending}
          disabled={!approving && reason.trim().length < 3}
          onClick={() => decide.mutate()}
        >
          {approving ? 'Approve and create loan' : TITLES[decision]}
        </Button>
      </div>
    </Modal>
  );
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'success';
}) {
  return (
    <div className="rounded-xl border border-border p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-foreground-secondary">
        {label}
      </p>
      <p
        className={cn(
          'display-figure mt-1 text-lg',
          tone === 'success' ? 'text-success' : 'text-foreground',
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-foreground-secondary">{hint}</p>}
    </div>
  );
}

function DetailRow({
  label,
  value,
  numeric,
  className,
}: {
  label: string;
  value: string | null | undefined;
  numeric?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs font-semibold uppercase tracking-wide text-foreground-secondary">
        {label}
      </p>
      <p
        className={cn(
          'mt-0.5 text-sm leading-relaxed text-foreground first-letter:uppercase',
          numeric && 'numeric',
        )}
      >
        {value || <span className="text-foreground-secondary">Not provided</span>}
      </p>
    </div>
  );
}
